/**
 * Cache storage management utilities
 * Handles localStorage operations with quota management and LRU eviction
 */

import { getAllCsvCacheKeys } from './cacheKeys.js';
// import { parseCacheKey } from './cacheKeys.js';

// Cache metadata structure
const CACHE_METADATA_KEY = 'csv-cache-metadata';
const MAX_CACHE_SIZE_MB = 8; // Conservative limit for localStorage
const MAX_CACHE_ENTRIES = 100; // Maximum number of cached entries

/**
 * Get cache metadata from localStorage
 * @returns {Object} Cache metadata object
 */
function getCacheMetadata() {
  try {
    const metadata = localStorage.getItem(CACHE_METADATA_KEY);
    return metadata ? JSON.parse(metadata) : {};
  } catch (error) {
    console.warn('Failed to parse cache metadata:', error);
    return {};
  }
}

/**
 * Update cache metadata in localStorage
 * @param {Object} metadata - Metadata object to store
 */
function setCacheMetadata(metadata) {
  try {
    localStorage.setItem(CACHE_METADATA_KEY, JSON.stringify(metadata));
  } catch (error) {
    console.warn('Failed to store cache metadata:', error);
  }
}

/**
 * Update access time for a cache entry
 * @param {string} cacheKey - Cache key to update
 */
function updateAccessTime(cacheKey) {
  const metadata = getCacheMetadata();
  if (!metadata[cacheKey]) {
    metadata[cacheKey] = { accessCount: 0 };
  }
  
  metadata[cacheKey].lastAccessed = Date.now();
  metadata[cacheKey].accessCount = (metadata[cacheKey].accessCount || 0) + 1;
  
  setCacheMetadata(metadata);
}

/**
 * Get estimated size of localStorage usage in bytes
 * @returns {number} Estimated size in bytes
 */
function getStorageSize() {
  let total = 0;
  for (let key in localStorage) {
    if (Object.prototype.hasOwnProperty.call(localStorage, key)) {
      total += localStorage[key].length + key.length;
    }
  }
  return total;
}

/**
 * Get storage size in MB
 * @returns {number} Size in MB
 */
function getStorageSizeMB() {
  return getStorageSize() / (1024 * 1024);
}

/**
 * Remove least recently used cache entries
 * @param {number} targetCount - Target number of entries to remove
 */
function evictLRUEntries(targetCount = 5) {
  const metadata = getCacheMetadata();
  const cacheKeys = getAllCsvCacheKeys();
  
  // Sort by last accessed time (oldest first)
  const sortedKeys = cacheKeys
    .map(key => ({
      key,
      lastAccessed: metadata[key]?.lastAccessed || 0,
      accessCount: metadata[key]?.accessCount || 0
    }))
    .sort((a, b) => {
      // Primary sort: last accessed time
      if (a.lastAccessed !== b.lastAccessed) {
        return a.lastAccessed - b.lastAccessed;
      }
      // Secondary sort: access count (less accessed first)
      return a.accessCount - b.accessCount;
    });
  
  // Remove oldest entries
  const toRemove = sortedKeys.slice(0, Math.min(targetCount, sortedKeys.length));
  
  toRemove.forEach(({ key }) => {
    try {
      localStorage.removeItem(key);
      delete metadata[key];
      console.log(`Evicted cache entry: ${key}`);
    } catch (error) {
      console.warn(`Failed to evict cache entry ${key}:`, error);
    }
  });
  
  setCacheMetadata(metadata);
  return toRemove.length;
}

/**
 * Check if storage is approaching limits
 * @returns {boolean} True if storage should be cleaned up
 */
function shouldEvictCache() {
  const sizeMB = getStorageSizeMB();
  const entryCount = getAllCsvCacheKeys().length;
  
  return sizeMB > MAX_CACHE_SIZE_MB || entryCount > MAX_CACHE_ENTRIES;
}

/**
 * Store data in cache with automatic cleanup if needed
 * @param {string} cacheKey - Cache key
 * @param {any} data - Data to store
 * @returns {boolean} True if successfully stored
 */
export function setCacheData(cacheKey, data) {
  const serializedData = JSON.stringify({
    data,
    timestamp: Date.now(),
    version: '1.0'
  });
  
  try {
    
    // Try to store the data
    localStorage.setItem(cacheKey, serializedData);
    updateAccessTime(cacheKey);
    
    // Check if we need to clean up after storing
    if (shouldEvictCache()) {
      evictLRUEntries(5);
    }
    
    return true;
  } catch (error) {
    if (error.name === 'QuotaExceededError') {
      console.warn('Storage quota exceeded, attempting cleanup...');
      
      // Try aggressive cleanup
      const evicted = evictLRUEntries(10);
      
      if (evicted > 0) {
        // Try storing again after cleanup
        try {
          localStorage.setItem(cacheKey, serializedData);
          updateAccessTime(cacheKey);
          return true;
        } catch (retryError) {
          console.error('Failed to store data even after cleanup:', retryError);
          return false;
        }
      }
    }
    
    console.error('Failed to store cache data:', error);
    return false;
  }
}

/**
 * Retrieve data from cache
 * @param {string} cacheKey - Cache key
 * @returns {any|null} Cached data or null if not found/invalid
 */
export function getCacheData(cacheKey) {
  try {
    const serializedData = localStorage.getItem(cacheKey);
    if (!serializedData) {
      return null;
    }
    
    const parsed = JSON.parse(serializedData);
    
    // Validate cache entry structure
    if (!parsed.data || !parsed.timestamp) {
      console.warn(`Invalid cache entry structure for key: ${cacheKey}`);
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    // Update access time
    updateAccessTime(cacheKey);
    
    return parsed.data;
  } catch (error) {
    console.warn(`Failed to retrieve cache data for key ${cacheKey}:`, error);
    // Clean up corrupted cache entry
    try {
      localStorage.removeItem(cacheKey);
    } catch (cleanupError) {
      console.warn('Failed to clean up corrupted cache entry:', cleanupError);
    }
    return null;
  }
}

/**
 * Check if data exists in cache
 * @param {string} cacheKey - Cache key
 * @returns {boolean} True if data exists in cache
 */
export function hasCacheData(cacheKey) {
  return localStorage.getItem(cacheKey) !== null;
}

/**
 * Remove specific cache entry
 * @param {string} cacheKey - Cache key to remove
 * @returns {boolean} True if successfully removed
 */
export function removeCacheData(cacheKey) {
  try {
    localStorage.removeItem(cacheKey);
    
    // Clean up metadata
    const metadata = getCacheMetadata();
    delete metadata[cacheKey];
    setCacheMetadata(metadata);
    
    return true;
  } catch (error) {
    console.warn(`Failed to remove cache entry ${cacheKey}:`, error);
    return false;
  }
}

/**
 * Clear all CSV cache data
 * @returns {number} Number of entries cleared
 */
export function clearAllCacheData() {
  const cacheKeys = getAllCsvCacheKeys();
  let cleared = 0;
  
  cacheKeys.forEach(key => {
    try {
      localStorage.removeItem(key);
      cleared++;
    } catch (error) {
      console.warn(`Failed to clear cache entry ${key}:`, error);
    }
  });
  
  // Clear metadata
  try {
    localStorage.removeItem(CACHE_METADATA_KEY);
  } catch (error) {
    console.warn('Failed to clear cache metadata:', error);
  }
  
  console.log(`Cleared ${cleared} cache entries`);
  return cleared;
}

/**
 * Get cache statistics
 * @returns {Object} Cache statistics
 */
export function getCacheStats() {
  const cacheKeys = getAllCsvCacheKeys();
  const metadata = getCacheMetadata();
  const sizeMB = getStorageSizeMB();
  
  let totalAccesses = 0;
  let oldestAccess = null;
  let newestAccess = null;
  
  cacheKeys.forEach(key => {
    const meta = metadata[key];
    if (meta) {
      totalAccesses += meta.accessCount || 0;
      if (meta.lastAccessed) {
        if (oldestAccess === null) {
          oldestAccess = meta.lastAccessed;
        } else {
          oldestAccess = Math.min(oldestAccess, meta.lastAccessed);
        }
        if (newestAccess === null) {
          newestAccess = meta.lastAccessed;
        } else {
          newestAccess = Math.max(newestAccess, meta.lastAccessed);
        }
      }
    }
  });
  
  return {
    entryCount: cacheKeys.length,
    totalSizeMB: Math.round(sizeMB * 100) / 100,
    maxSizeMB: MAX_CACHE_SIZE_MB,
    maxEntries: MAX_CACHE_ENTRIES,
    totalAccesses,
    oldestAccess: oldestAccess ? new Date(oldestAccess) : null,
    newestAccess: newestAccess ? new Date(newestAccess) : null,
    utilizationPercent: Math.round((sizeMB / MAX_CACHE_SIZE_MB) * 100)
  };
}
